import React, { useEffect, useRef, useState } from "react";
import DownArrow from "../assets/down-arrow.svg";
import UpArrow from "../assets/up-arrow.svg";

interface DropdownProps {
  label?: React.ReactNode;
  options: { value: string | number; label: string; disabled?: boolean }[];
  selectedValues: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  isSingleSelect?: boolean;
  className?: string; // Add this line
  showSelectAll?: boolean; // Add this line for select all feature
}

const Dropdown: React.FC<DropdownProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = "Select options",
  searchable = false,
  disabled = false,
  onClose,
  onOpen,
  isSingleSelect = false, // Default to false (multi-select by default)
  className = "", // Add this line
  showSelectAll = false, // Add this line for select all feature
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isDropdownOpen) {
          setIsDropdownOpen(false);
          if (onClose) onClose();
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isDropdownOpen, onClose]);

  const handleOptionClick = (value: string | number) => {
    const newSelectedValues = isSingleSelect
      ? [value] // Only allow one selection
      : selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value) // Deselect if already selected
      : [...selectedValues, value]; // Select multiple

    onChange(newSelectedValues);
    if (isSingleSelect) {
      setIsDropdownOpen(false); // Close dropdown after selecting one option
    }
  };

  // Add select all functionality
  const handleSelectAll = () => {
    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const filteredValues = filteredOptions.map((option) => option.value);
    
    // Check if all filtered options are already selected
    const allSelected = filteredValues.every((value) => selectedValues.includes(value));
    
    if (allSelected) {
      // If all are selected, deselect all filtered options
      const newSelectedValues = selectedValues.filter((value) => !filteredValues.includes(value));
      onChange(newSelectedValues);
    } else {
      // If not all are selected, select all filtered options
      const newSelectedValues = Array.from(new Set([...selectedValues, ...filteredValues]));
      onChange(newSelectedValues);
    }
  };

  // Check if all filtered options are selected
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredValues = filteredOptions.map((option) => option.value);
  const allFilteredSelected = filteredValues.length > 0 && filteredValues.every((value) => selectedValues.includes(value));

  return (
    <div
      ref={dropdownRef}
      className={`relative w-full ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      }`}
    >
      <label className="block text-10 text-gray-500 pb-1">{label}</label>
      <div
        className={`h-10 bg-white flex justify-between items-center cursor-pointer border-2 border-gray-400 px-2 rounded ${className}`}
        onClick={() => {
          if (!disabled) {
            setIsDropdownOpen((prevState) => {
              if (prevState) {
                if (onClose) onClose();
                return !prevState;
              }
              if (onOpen) onOpen();
              return !prevState;
            });
          }
        }}
      >
        <span className="text-sm font-medium truncate">
          {selectedValues.length > 0
            ? selectedValues
                .slice(0, 1)
                .map(
                  (value) =>
                    options.find((option) => option.value === value)?.label ||
                    ""
                )
                .join(", ") +
              (!isSingleSelect && selectedValues.length > 1
                ? `, +${selectedValues.length - 1} more`
                : "")
            : placeholder}
        </span>
        <span>
          <img src={isDropdownOpen ? UpArrow : DownArrow} alt="" />
        </span>
      </div>
      {isDropdownOpen && (
        <div className="absolute max-h-[300px] h-[140px] md:h-auto overflow-auto top-full left-0 mt-1 w-full bg-white border rounded shadow-lg z-40">
          {searchable && (
            <div className="p-2">
              <input
                type="text"
                placeholder="Search..."
                className="w-full p-1 border rounded"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
          <ul className="py-1">
            {/* Add Select All option */}
            {showSelectAll && !isSingleSelect && filteredOptions.length > 0 && (
              <li>
                <button
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 font-semibold border-b border-gray-200 ${
                    allFilteredSelected ? "bg-blue-100 text-blue-700" : ""
                  }`}
                  onClick={handleSelectAll}
                >
                  {allFilteredSelected ? "Deselect All" : "Select All"}
                </button>
              </li>
            )}
            {options
              .filter((option) =>
                option.label.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((option) => (
                <li key={option.value}>
                  <button
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      selectedValues.includes(option.value)
                        ? "font-semibold bg-gray-100"
                        : ""
                    } ${option.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => !option.disabled && handleOptionClick(option.value)}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
